/**
 * useModerationNavigationGuard hook
 *
 * Guards against accidental navigation away from the moderation work queue
 * when the user has unsubmitted reports. Uses React Router v7's `useBlocker`
 * for in-app navigation and the `beforeunload` event for tab/browser closes.
 */

import { useEffect } from 'react';
import { useBlocker } from 'react-router-dom';

interface NavigationGuardOptions {
  /** Whether the user has unsaved/unsubmitted work */
  hasUnsavedWork: boolean;
}

/**
 * Blocks navigation (in-app and browser-level) when the user has unsubmitted
 * moderation reports.
 *
 * @returns An object containing the React Router `blocker` (with `state`,
 *          `proceed()`, and `reset()` methods) for rendering a confirmation dialog.
 */
export function useModerationNavigationGuard({ hasUnsavedWork }: NavigationGuardOptions) {
  const blocker = useBlocker(hasUnsavedWork);

  // Guard against browser/tab close or external navigation
  useEffect(() => {
    if (!hasUnsavedWork) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = ''; // required for Chrome/Edge
    };

    window.addEventListener('beforeunload', handler);

    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedWork]);

  return { blocker };
}
